import { MAIL_LIMIT_DEFAULTS } from './mail.js';
// Value imports, not type-only ones: `isAdminAction` reads its list at
// runtime, and the workspace defaults below are real values.
import { ASSISTANT_ADMIN_ACTION_KINDS, BOOKING_DEFAULT_MAX_DAYS_AHEAD, COFFEE_DEFAULT_DAYS, COFFEE_DEFAULT_TIME, } from './constants.js';
export const WORKSPACE_FEATURES = [
    'chat',
    'meetings',
    'games',
    'spaces',
    'tasks',
    'home',
    'assistant',
    'company',
    /** Streaks, kudos, the points store and focus sessions — the retention loop. */
    'engagement',
    /**
     * Hosted email: the workspace's own domains, and the mailboxes on them.
     *
     * A product like any other, so a workspace that does not buy mail never sees
     * the screen — but unlike the rest it also costs the platform a seat, so the
     * feature being on is necessary and not sufficient. The plan limits decide
     * the rest.
     */
    'mail',
    /** Daily micro-lessons written from what social media is discussing. */
    'learning',
    /** Brand and competitor watch, the industry news brief and the trend radar. */
    'insights',
];
/** How an account with no invitation may end up inside a workspace. */
export const JOIN_POLICIES = ['approval', 'open', 'invite-only'];
export const DEFAULT_WORKSPACE_SETTINGS = {
    disabledFeatures: [],
    joinPolicy: 'approval',
    allowedEmailDomains: [],
    // Off by default, and off is the honest default: translation needs an
    // engine configured for the deployment, and a switch that is on while
    // nothing answers it is a promise the workspace cannot keep.
    translation: { enabled: false, automatic: true },
    // Off until an owner picks an emoji: a reaction that silently spends one
    // of somebody's five daily Appys is a surprise, not a feature.
    engagement: { kudosEmoji: null },
    booking: { maxDaysAhead: BOOKING_DEFAULT_MAX_DAYS_AHEAD },
    // Off until an owner asks for it: the draw writes into people's DMs, and a
    // workspace should not find that happening because it upgraded.
    coffee: {
        enabled: false,
        days: [...COFFEE_DEFAULT_DAYS],
        time: COFFEE_DEFAULT_TIME,
    },
    timezone: '',
    country: '',
    currency: 'USD',
    industry: 'general',
    spaceLimit: 0,
    allowedTemplates: [],
    // Small on purpose. Mail is the one product here where an unbounded default
    // is also an unbounded spam liability, so a workspace nobody has made a
    // decision about gets a starter allowance rather than no ceiling.
    mail: { ...MAIL_LIMIT_DEFAULTS },
};
export const SPACE_ACTIVITIES = [
    'tasks',
    'projects',
    'files',
    'whiteboards',
    'meetings',
    'people',
    'chat',
    'games',
    'library',
];
/**
 * Whether a participant on the floor is a space agent rather than a person.
 *
 * An agent has no socket, so the server fills the required `socketId` with an
 * `agent:` marker nothing else is allowed to wear. That marker is the whole
 * distinction, and it travels with the participant — which is what lets a
 * client draw an agent differently without a lookup.
 */
export const isAgentParticipant = (participant) => participant.socketId.startsWith('agent:');
/**
 * Everything the activity feed can point at. The first half is collaboration
 * content; the rest are administration records, added so the audit view can
 * answer "who changed what" about the workspace itself.
 */
export const ACTIVITY_TARGET_TYPES = [
    'task',
    'project',
    'space',
    'channel',
    'whiteboard',
    'meeting',
    'game',
    'org',
    'member',
    'announcement',
    'leave',
    'survey',
    'department',
    'policy',
    'budget',
    'expense',
    'statement',
    'mailDomain',
    'mailbox',
    /* The ERP surfaces inside the company tab: buying, selling, owning and
       employing. Each names the record acted on, so the audit trail reads as
       one story rather than as several feeds that happen to share a page. */
    'vendor',
    'purchaseOrder',
    'customer',
    'deal',
    'invoice',
    'asset',
    'stockItem',
    'payRun',
    'compensation',
    'employee',
    'opening',
    'candidate',
    'reviewCycle',
    'review',
];
/**
 * Which half of someone's life a reminder belongs to.
 *
 * The same two words meetings already file themselves under, so "personal"
 * means the same thing on the calendar and in the reminders section rather
 * than being a second vocabulary for the same distinction.
 */
export const REMINDER_CATEGORIES = ['personal', 'work'];
/**
 * Who a library file is for.
 *
 * `workspace` is the old behaviour and stays the default: everyone in the org
 * sees it. `shared` narrows it to named colleagues, and `private` keeps it to
 * the person who uploaded it — a secret file is absent from everyone else's
 * list, admins included, so "secret" means the same thing to the person
 * relying on it as it does to the database.
 */
export const FILE_VISIBILITIES = ['workspace', 'shared', 'private'];
/**
 * The kinds of file the library can make from nothing, as opposed to receive
 * from a phone's file picker.
 *
 * Each one is a real upload *and* a live document: the bytes are what makes
 * it a row in the library, shareable and downloadable like anything else,
 * and the live copy is what two people actually type in. Creating one is
 * therefore not "make an empty file" — it is "make the file and the thing
 * everyone edits", which is why this is a kind rather than a file extension.
 */
export const LIBRARY_FILE_KINDS = ['doc', 'sheet', 'slides'];
/**
 * How much the assistant should write when it drafts a library file.
 *
 * A length rather than a word count: "about two pages" means a different
 * number of words in a deck than in a report, and the person asking is
 * thinking in pages, not in tokens.
 */
export const GENERATED_FILE_LENGTHS = ['brief', 'standard', 'detailed'];
/**
 * Room for the brief.
 *
 * Long enough to paste the requirements someone was sent, short enough that
 * the brief cannot crowd the project context out of the same request.
 */
export const GENERATED_FILE_BRIEF_MAX = 2000;
/* --------------------------------- vault -------------------------------- */
/**
 * How long a PIN may be. Digits only, so it can be typed on a keypad with a
 * thumb — the vault is a lock on a phone screen, not a passphrase field.
 */
export const VAULT_PIN_MIN_LENGTH = 4;
export const VAULT_PIN_MAX_LENGTH = 8;
/**
 * The shelves inside a vault.
 *
 * A fixed set rather than the library's own categories, which are
 * workspace-wide and managed by admins — neither of which can be true of a
 * drawer nobody else can open. These are the things people actually put away:
 * papers, plastic, proof of who they are, proof of what they own, the strings
 * that unlock other things, and pictures.
 */
export const VAULT_CATEGORIES = [
    'documents',
    'cards',
    'ids',
    'assets',
    'keys',
    'photos',
    'other',
];
/**
 * The shelf a file lands on when nobody says otherwise.
 *
 * A guess from the file itself, so putting something away is one tap rather
 * than a form. It is only ever a default — the shelf is a field the owner can
 * change afterwards.
 */
export function vaultCategoryFor(mimeType, name) {
    if (mimeType.startsWith('image/'))
        return 'photos';
    if (mimeType.startsWith('text/') ||
        mimeType === 'application/pdf' ||
        mimeType.includes('word') ||
        mimeType.includes('spreadsheet') ||
        mimeType.includes('excel') ||
        /\.(?:pdf|docx?|md|markdown|txt|rtf|pages|xlsx?|csv|tsv|pptx?|odt)$/i.test(name)) {
        return 'documents';
    }
    /* A `.pem`, a `.key` or a recovery-codes file is the one non-document kind
       worth guessing: it is the thing people are most alarmed to misfile. */
    if (/\.(?:pem|key|kdbx|p12|pfx|asc|gpg|ovpn)$/i.test(name))
        return 'keys';
    return 'other';
}
/**
 * The kinds of thing you can type into a vault, as opposed to upload.
 *
 * Three, because they are the three shapes people actually have: a way into
 * an account, a piece of plastic, and everything else in prose.
 */
export const VAULT_ENTRY_KINDS = ['login', 'card', 'note'];
export const WHITEBOARD_SCOPES = ['free', 'space', 'meeting'];
/* ------------------------------------------------------------------ */
/* Meetings                                                            */
/* ------------------------------------------------------------------ */
export const MEETING_CATEGORIES = ['work', 'personal'];
/**
 * The kinds of governing document a workspace publishes. Split by kind rather
 * than left as free-form titles so the reader can be pointed at "the code of
 * ethics" and the acknowledgement report can be filed by what was signed.
 */
export const POLICY_KINDS = ['policy', 'code-of-ethics', 'handbook'];
export const LEAVE_KINDS = ['vacation', 'sick', 'personal', 'other'];
export const LEAVE_STATUSES = ['pending', 'approved', 'rejected'];
/* ------------------------------ celebrations --------------------------- */
export const CELEBRATION_KINDS = ['birthday', 'anniversary'];
/* ------------------------------------------------------------------ */
/* Finance                                                             */
/* ------------------------------------------------------------------ */
/**
 * Currencies a workspace can keep its books in.
 *
 * Deliberately only two-decimal currencies: every amount is stored as an
 * integer number of minor units on a fixed 100:1 assumption, so a zero-decimal
 * currency (JPY) or a three-decimal one (KWD) would silently mis-scale each
 * figure by a factor of a hundred or ten. Keeping the list honest is cheaper
 * than carrying a per-currency exponent through the schema, the routes and
 * every total on the screen.
 */
export const CURRENCIES = [
    'USD',
    'EUR',
    'GBP',
    'AED',
    'SAR',
    'EGP',
    'CAD',
    'AUD',
    'CHF',
    'SEK',
    'INR',
    'TRY',
    'ZAR',
    'BRL',
    'MXN',
];
export const EXPENSE_CATEGORIES = [
    'payroll',
    'software',
    'hardware',
    'office',
    'travel',
    'marketing',
    'services',
    'training',
    'other',
];
/**
 * Where a budget or an expense is charged. `workspace` is the whole company;
 * the other two point at a department in the org chart or at one project
 * board, which are the two units the money is actually argued about in.
 */
export const FINANCE_SCOPES = ['workspace', 'department', 'project'];
export const EXPENSE_STATUSES = ['pending', 'approved', 'rejected'];
export const STATEMENT_KINDS = [
    'income-statement',
    'balance-sheet',
    'cash-flow',
    'budget-plan',
    'tax',
    'audit',
    'invoice',
    'other',
];
/** What a document is being read for, and so which fields come back. */
export const FINANCE_READ_TARGETS = ['expense', 'statement'];
/* ------------------------------------------------------------------ */
/* Physical office                                                     */
/* ------------------------------------------------------------------ */
/** How an office check-in was made. */
export const OFFICE_CHECKIN_METHODS = ['manual', 'geofence'];
/* ------------------------------------------------------------------ */
/* Itsappy AI assistant                                                */
/* ------------------------------------------------------------------ */
/**
 * What a batch of suggested names is *for*. The assistant needs this to know
 * which conventions apply — a project wants a short key alongside the name, a
 * channel wants lowercase-and-hyphens, a workspace wants something a whole
 * company will live with.
 */
export const ASSISTANT_SUBJECTS = ['workspace', 'project', 'space', 'channel', 'team'];
/**
 * How far back a conversation travels with an in-chat question.
 *
 * A window in time rather than a message count: "what happened this week" is
 * how people actually think about a room, and the old fixed twenty messages
 * could not reach past whatever the client happened to have loaded.
 */
export const CHAT_CONTEXT_RANGES = ['day', 'week', 'month', 'quarter', 'all'];
/** How much work a suggested feature looks like, before anyone estimates it. */
export const FEATURE_EFFORTS = ['small', 'medium', 'large'];
/**
 * What a space agent is *for*. The role picks the system prompt, so this is
 * closer to a job description than a label — an agent is only as useful as the
 * role it was given.
 */
export const SPACE_AGENT_ROLES = [
    'facilitator',
    'note-taker',
    'standup-host',
    'onboarding-buddy',
    'researcher',
    'planner',
    'triage',
    'receptionist',
    'devils-advocate',
    'coach',
    'editor',
    'custom',
];
/** Whether a proposal is one only an owner may be shown, let alone run. */
export function isAdminAction(action) {
    return ASSISTANT_ADMIN_ACTION_KINDS.includes(action.kind);
}
/** How many past turns travel with a new question. */
export const AGENT_THREAD_CONTEXT_TURNS = 20;
/* ------------------------------------------------------------------ */
/* Project agents                                                      */
/* ------------------------------------------------------------------ */
/**
 * A document a project agent reads before it plans anything.
 *
 * Two kinds because a project's requirements live in two places already: a
 * live doc somebody is still editing, and an upload somebody was sent — a
 * signed spec, a change request as a PDF. Both are referenced rather than
 * copied, so the agent reads what the document says today, not what it said
 * when it was attached.
 */
export const PROJECT_AGENT_SOURCE_KINDS = ['doc', 'file'];
/**
 * What the agent concluded about work somebody called finished.
 *
 * Three outcomes rather than a pass/fail, because "finished" is usually
 * partly true: the useful answer to a closed task is which part of the
 * requirement it met and which part is still owed.
 */
export const AGENT_REVIEW_VERDICTS = ['meets', 'partial', 'gaps', 'unclear'];
/** Where a review's suggestions have got to. */
export const AGENT_REVIEW_STATES = ['proposed', 'accepted', 'dismissed'];
/** What triggered a review: a task closing, or somebody handing over a change. */
export const AGENT_REVIEW_TRIGGERS = ['task_completed', 'request'];
/** How many documents one agent may be pointed at. */
export const PROJECT_AGENT_MAX_SOURCES = 8;
/**
 * How much of each document travels with a review.
 *
 * A requirements doc can be longer than the model's whole budget, and a
 * review that reads the first few pages of every source is worth more than
 * one that reads all of the first source and none of the rest.
 */
export const PROJECT_AGENT_SOURCE_CHARS = 6000;
/** How many follow-ups one review may propose. */
export const PROJECT_AGENT_MAX_FOLLOW_UPS = 6;
/**
 * How many reviews one project's agent may run in a day.
 *
 * Every review is a model call a workspace pays for, and a team that closes
 * forty tasks in an afternoon does not want forty of them. The cap is per
 * project so a busy board cannot spend another board's budget.
 */
export const PROJECT_AGENT_DAILY_REVIEW_LIMIT = 40;
/** The label a task filed by an agent carries, so a board can see which are its. */
export const AGENT_TASK_LABEL = 'agent-planned';
/* ------------------------------------------------------------------ */
/* Service API keys                                                    */
/* ------------------------------------------------------------------ */
/**
 * What a key is allowed to reach. Deliberately narrow: a key is a standing
 * credential with no second factor and no session to expire, so it opens one
 * named surface rather than "the API". Every route that accepts keys names the
 * scopes it honours — anything unnamed refuses keys outright.
 */
export const API_KEY_SCOPES = ['meetings:read', 'meetings:write', 'meetings:guests'];
